import BaseDetailView from '../shared/BaseDetailView';
import Badge from '../shared/Badge';
import DetailViewActionButtons from '../shared/DetailViewActionButtons';
import NavigationArrow from '../shared/NavigationArrow';
import ProductAccessTab from './ProductAccessTab';
import DetailsTabLayout, { FieldSection, SectionDivider } from '../shared/DetailsTabLayout';
import ProductActivityTab from './ProductActivityTab';
import { useEditableState } from '../../hooks/useEditableState';
import { useDetailViewActions } from '../../hooks/useDetailViewActions.jsx';
import { useToggleField } from '../../hooks/useToggleField';
import { ICONS } from '../../constants/detailViewConstants';
import { getSubTheme } from '../../constants/subThemes';

export default function ProductDetailView({ product, onUpdate, onDelete, onCreateNew, onPrevious = null, onNext = null, theme = 'purple' }) {
  const themeConfig = getSubTheme(theme);

  const {
    isEditMode,
    isSaving,
    editedData: editedProduct,
    handleSave,
    handleCancel,
    handleEdit,
    updateField,
    toggleField
  } = useEditableState(product, onUpdate);

  const {
    createDeleteAction,
    createExportAction
  } = useDetailViewActions();

  // Handle activate/deactivate
  const handleToggleActive = useToggleField(product, 'is_active', onUpdate, 'Failed to toggle active state');

  // Create action buttons for the header
  const headerActions = product ? [
    {
      icon: product?.is_active
        ? 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z'
        : 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      label: product?.is_active ? 'Deactivate' : 'Activate',
      onClick: handleToggleActive,
      variant: product?.is_active ? 'danger' : 'success'
    },
    createExportAction(product, `product-${product.name?.replace(/\s+/g, '-').toLowerCase()}.json`, 'Export product data'),
    ...(onDelete ? [createDeleteAction(
      () => onDelete(product.id),
      `Are you sure you want to delete ${product.name}? This action cannot be undone.`,
      'Delete product'
    )] : [])
  ] : [];

  // Header content with icon, title, and badges
  const headerContent = (
    <div className="flex items-center space-x-4">
      {/* Navigation Arrow - Previous */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="previous"
          onClick={onPrevious}
          disabled={!onPrevious}
          label={onPrevious ? 'Previous product' : 'No previous product'}
        />
      )}

      <div className="p-3 bg-purple-500/20 rounded-lg" style={{ color: 'var(--color-text-on-accent)' }}>
              <svg className="w-8 h-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      </div>
      <div>
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{product?.name}</h2>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{product?.category}</span>
          <span className="text-sm" style={{ color: 'var(--color-text-disabled)' }}>•</span>
          <Badge variant="gray">{product?.version || 'no version'}</Badge>
          <Badge variant="gray">
            {product?.is_active ? 'active' : 'inactive'}
          </Badge>
          <Badge variant="gray">
            {product?.collection_count || 0} {product?.collection_count === 1 ? 'collection' : 'collections'}
          </Badge>
        </div>
      </div>

      {/* Navigation Arrow - Next */}
      {(onPrevious || onNext) && (
        <NavigationArrow
          direction="next"
          onClick={onNext}
          disabled={!onNext}
          label={onNext ? 'Next product' : 'No next product'}
        />
      )}
    </div>
  );

  // Custom tabs for products
  const tabs = [
    { id: 'details', label: 'Details', icon: ICONS.details },
    { id: 'access', label: 'Access', icon: ICONS.access },
    { id: 'activity', label: 'Activity', icon: ICONS.activity }
  ];

  // Render content based on active tab
  const renderContent = (activeTab) => {
    if (activeTab === 'details') {
      return (
        <DetailsTabLayout
          title="Product Details"
          subtitle="Manage product information and settings"
          isEditMode={isEditMode}
          isSaving={isSaving}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={handleCancel}
        >
          <FieldSection label="Product Name">
            <input
              type="text"
              value={isEditMode ? (editedProduct?.name || '') : (product?.name || '')}
              onChange={(e) => updateField('name', e.target.value)}
              disabled={!isEditMode}
              className={`w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none ${themeConfig.focus} focus:bg-gray-800 transition-all disabled:opacity-50`}
            />
          </FieldSection>

          <SectionDivider />

          <FieldSection label="Version">
            <input
              type="text"
              value={isEditMode ? (editedProduct?.version || '') : (product?.version || '')}
              onChange={(e) => updateField('version', e.target.value)}
              disabled={!isEditMode}
              className={`w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none ${themeConfig.focus} focus:bg-gray-800 transition-all disabled:opacity-50`}
            />
          </FieldSection>

          <SectionDivider />

          <FieldSection label="Category">
            <input
              type="text"
              value={isEditMode ? (editedProduct?.category || '') : (product?.category || '')}
              onChange={(e) => updateField('category', e.target.value)}
              disabled={!isEditMode}
              className={`w-full h-11 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 text-white text-sm focus:outline-none ${themeConfig.focus} focus:bg-gray-800 transition-all disabled:opacity-50`}
            />
          </FieldSection>
        </DetailsTabLayout>
      );
    }

    if (activeTab === 'access') {
      return <ProductAccessTab productId={product?.id} />;
    }

    if (activeTab === 'activity') {
      return <ProductActivityTab productId={product?.id} />;
    }

    return null;
  };

  return (
    <BaseDetailView
      item={product}
      emptyStateIcon={ICONS.product}
      emptyStateTitle="No product selected"
      emptyStateSubtitle="Select a product from the sidebar to view details"
      emptyStateAction={onCreateNew ? { label: 'Create New', onClick: onCreateNew } : null}
      headerContent={headerContent}
      headerActions={<DetailViewActionButtons actions={headerActions} />}
      tabs={tabs}
      defaultTab="details"
      renderContent={renderContent}
    />
  );
}
